import { useEffect, useState } from 'react';
import { useNoticeToast } from '../lib/toast';
import { api, uploadDriverNoticeImage, type DriverNoticePreview, type DriverNoticeTarget } from '../lib/api';
import { ErrorBox } from '../components/DataTable';

/**
 * 기사개인오더 ⑤ — 발행 알림톡(계산서·페이앱 이미지 → 신청 기사). 기획 `plan/driver-orders.md` ⑤.
 * 기사접수 시트 「DB_image」에 번호·이미지 링크를 적던 일을 대신한다. 계산서(페이앱) 창 안에 붙는다.
 * #340 — 번호가 들어 있고 보낼 수 있는 시간이면 이미지를 고르는 순간(올린 뒤) 바로 보낸다. 받는 번호는 파일 칸 아래에 미리 보인다.
 *   번호가 없거나 시간 밖이면 예전처럼 버튼 두 번(번호를 한 번 더 보이고) — 알림톡은 되돌릴 수 없다.
 */

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const fmtPhone = (p: string) => p.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');

export function DriverNoticePanel({ target, id, onSent }: { target: DriverNoticeTarget; id: number; onSent?: () => void }) {
  const [data, setData] = useState<DriverNoticePreview | null>(null);
  const [phone, setPhone] = useState('');
  const [image, setImage] = useState<{ path: string; type: string; name: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);
  const [arm, setArm] = useState(false);
  const [showText, setShowText] = useState(false);

  async function load(keepPhone = false) {
    try {
      const r = await api.previewDriverNotice({ target, id });
      setData(r);
      if (!keepPhone) setPhone(r.defaultPhone ?? '');
    } catch (e) { setError(errMsg(e)); }
  }
  useEffect(() => { void load(); }, [target, id]);

  async function pick(file: File | undefined, input?: HTMLInputElement) {
    if (!file) return;
    const auto = canAuto; // 고른 순간의 번호·시간으로 정한다
    setBusy('이미지를 올리는 중…'); setError(null); setNotice(null); setArm(false);
    let up: { path: string; type: string; name: string } | null = null;
    try { up = await uploadDriverNoticeImage(file); setImage(up); } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
    if (input) input.value = ''; // 같은 파일을 다시 골라도 onChange 가 오게
    if (up && auto) await deliver(up);
  }
  async function send() {
    if (!arm) { setArm(true); return; }
    setArm(false);
    await deliver(image!);
  }
  async function deliver(img: { path: string; type: string; name: string }) {
    setBusy('보내는 중…'); setError(null); setNotice(null);
    try {
      const r = await api.sendDriverNotice({ target, id, phone, image: img });
      setNotice(`알림톡을 보냈습니다(${/amend|02$/.test(r.templateCode) ? '이미지 변경' : '첫 발송'}) — ${fmtPhone(phone.replace(/\D/g, ''))}`);
      setImage(null);
      await load(true);
      onSent?.();
    } catch (e) { setError(errMsg(e)); await load(true); } finally { setBusy(null); }
  }

  const digits = phone.replace(/\D/g, '');
  const phoneOk = /^01\d{8,9}$/.test(digits);
  const canAuto = phoneOk && !!data?.inHours && !data?.cancelled && !data?.blockedWhy;
  if (!data) return error ? <ErrorBox message={error} /> : <p className="text-sm text-ink-faint">알림톡 정보를 불러오는 중…</p>;
  return (
    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/40 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold">알림톡 — {data.kind} 이미지를 신청 기사에게</h3>
        <span className="text-xs text-ink-soft">{data.amend ? '다음 발송 = 「이미지 변경」 알림' : '아직 안 보냄'}</span>
      </div>
      {data.cancelled ? <p className="text-red-700">취소된 건이라 보내지 않습니다.</p> : data.blockedWhy ? <p className="text-amber-800">{data.blockedWhy}</p> : (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="space-y-1">받을 기사 휴대폰
              <input className="field w-full" value={phone} onChange={(e) => { setPhone(e.target.value); setArm(false); }} placeholder="010-0000-0000" inputMode="tel" />
            </label>
            <label className="space-y-1">계산서 이미지(JPG·PNG·PDF)
              <input className="field w-full" type="file" accept="image/jpeg,image/png,application/pdf" onChange={(e) => void pick(e.target.files?.[0], e.target)} disabled={!!busy} />
              <span className={`block text-[11px] ${canAuto ? 'text-brand-700' : 'text-ink-faint'}`}>
                {canAuto ? `고르시면 올린 뒤 바로 ${fmtPhone(digits)} 로 보냅니다` : '번호를 넣고 09~21시면 고르는 즉시 보냅니다 — 지금은 아래 버튼으로'}
              </span>
            </label>
          </div>
          {data.candidates.length > 0 && (
            <div className="flex flex-wrap gap-1 text-xs">
              <span className="text-ink-soft">신청자 「{data.requester}」 로 찾은 기사:</span>
              {data.candidates.map((c) => (
                <button key={c.driver_id} type="button" className={`rounded border px-1.5 py-0.5 ${c.phone && c.phone === digits ? 'border-brand-500 bg-brand-50' : 'bg-white'}`}
                  disabled={!c.phone} onClick={() => { setPhone(c.phone ?? ''); setArm(false); }}
                  title={c.car_match ? '차량번호까지 맞음' : '이름만 맞음 — 동명이인일 수 있습니다'}>
                  {c.driver_name} · {c.car_no ?? '차량 없음'} · {c.phone ? fmtPhone(c.phone) : '번호 없음'}{c.car_match ? ' ✓' : ''}
                </button>
              ))}
            </div>
          )}
          {data.candidates.length === 0 && <p className="text-xs text-amber-800">신청자 「{data.requester ?? '—'}」 와 이름이 같은 기사를 못 찾았습니다 — 번호를 직접 넣으세요.</p>}
          {!data.inHours && <p className="text-xs text-red-700">알림톡은 09시~21시에만 보냅니다(기사접수 시트와 같다).</p>}
          <div className="flex flex-wrap items-center gap-2">
            <button className={`btn-primary ${arm ? 'bg-red-600' : ''}`} onClick={() => void send()} onBlur={() => setArm(false)}
              disabled={!!busy || !image || !phoneOk || !data.inHours}>
              {arm ? `정말 보내기 → ${fmtPhone(digits)}` : '알림톡 보내기'}
            </button>
            {image && <span className="text-xs text-ink-soft">올림: {image.name}</span>}
            {!image && <span className="text-xs text-ink-faint">이미지를 먼저 고르세요</span>}
            <button type="button" className="ml-auto text-xs text-brand-700 underline" onClick={() => setShowText((v) => !v)}>{showText ? '문구 닫기' : '보낼 문구 보기'}</button>
          </div>
          {showText && <pre className="whitespace-pre-wrap rounded bg-white p-2 text-xs">{data.preview}</pre>}
        </>
      )}
      {busy && <p className="text-ink-soft">{busy}</p>}
      {error && <ErrorBox message={error} />}
      {data.history.length > 0 && (
        <table className="w-full text-xs">
          <thead><tr className="text-left text-ink-soft"><th>보낸 때</th><th>번호</th><th>종류</th><th>결과</th><th>보낸 사람</th><th>보낸 파일</th></tr></thead>
          <tbody>
            {data.history.map((h) => (
              <tr key={h.notify_id} className="border-t">
                <td className="py-0.5">{h.created_at}</td><td>{fmtPhone(h.phone)}</td>
                <td>{h.template_code === 'fineimageamend' ? '이미지 변경' : '첫 발송'}</td>
                <td className={h.status === 'sent' ? 'text-emerald-700' : 'text-red-700'} title={h.error ?? ''}>{h.status === 'sent' ? '보냄' : `실패 ${h.error ?? ''}`.slice(0, 40)}</td>
                <td>{h.created_by?.split('@')[0]}</td>
                <td>{h.view_url
                  ? <a className="text-brand-700 underline" href={h.view_url} target="_blank" rel="noreferrer" title={h.image_name ?? ''}>파일 보기</a>
                  : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
